<template>
  <div class="form-field enhanced" :class="fieldClasses">
    <label :for="fieldId" class="form-label">
      {{ label }}
      <span v-if="required" class="required-indicator">*</span>
    </label>
    <div class="form-input-wrapper">
      <input
        v-if="type !== 'select' && type !== 'checkbox' && type !== 'textarea'"
        :id="fieldId"
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        class="form-input"
        @blur="validateField"
        @input="onInput"
        @focus="onFocus"
        @keydown="onKeydown"
      />
      <textarea
        v-else-if="type === 'textarea'"
        :id="fieldId"
        :value="modelValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        class="form-input form-textarea"
        rows="4"
        @blur="validateField"
        @input="onInput"
        @focus="onFocus"
      ></textarea>
      <select
        v-else-if="type === 'select'"
        :id="fieldId"
        :value="modelValue"
        :required="required"
        :disabled="disabled"
        class="form-input form-select"
        @blur="validateField"
        @change="onChange"
        @focus="onFocus"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <div v-else-if="type === 'checkbox'" class="form-checkbox-wrapper">
        <input
          :id="fieldId"
          :checked="modelValue"
          type="checkbox"
          :required="required"
          :disabled="disabled"
          class="form-checkbox"
          @change="onCheckboxChange"
          @focus="onFocus"
        />
        <label :for="fieldId" class="checkbox-label">{{ checkboxLabel }}</label>
      </div>
      <div v-if="loading" class="input-loading">
        <span class="loading-spinner"></span>
      </div>
      <div v-if="showPasswordToggle && type === 'password'" class="password-toggle" @click="togglePasswordVisibility">
        <span class="password-toggle-icon">{{ passwordVisible ? '👁️' : '👁️‍🗨️' }}</span>
      </div>
    </div>
    <div v-if="error" class="form-error" role="alert" :aria-live="ariaLive">{{ error }}</div>
    <div v-if="success" class="form-success" role="status" :aria-live="ariaLive">{{ success }}</div>
    <div v-if="helpText" class="form-help">{{ helpText }}</div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'

export default {
  name: 'EnhancedFormField',
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    success: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    checkboxLabel: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    showPasswordToggle: {
      type: Boolean,
      default: false
    },
    validation: {
      type: Function,
      default: null
    },
    ariaLive: {
      type: String,
      default: 'polite'
    }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'validate'],
  setup(props, { emit }) {
    const passwordVisible = ref(false)
    const focused = ref(false)
    
    const fieldId = computed(() => `field-${Math.random().toString(36).substr(2, 9)}`)
    
    const fieldClasses = computed(() => ({
      'has-error': !!props.error,
      'has-success': !!props.success,
      'is-loading': props.loading,
      'is-focused': focused.value,
      'is-disabled': props.disabled,
      'is-required': props.required,
      [`field-type-${props.type}`]: true
    }))
    
    const togglePasswordVisibility = () => {
      passwordVisible.value = !passwordVisible.value
    }
    
    const validateField = () => {
      if (props.validation && typeof props.validation === 'function') {
        const result = props.validation(props.modelValue)
        emit('validate', result)
      }
      focused.value = false
      emit('blur')
    }
    
    const clearErrors = () => {
      emit('validate', { isValid: true, error: '' })
    }
    
    const onFocus = () => {
      focused.value = true
      emit('focus')
    }
    
    const onInput = (event) => {
      emit('update:modelValue', event.target.value)
      clearErrors()
    }
    
    const onChange = (event) => {
      emit('update:modelValue', event.target.value)
      clearErrors()
    }
    
    const onCheckboxChange = (event) => {
      emit('update:modelValue', event.target.checked)
      clearErrors()
    }
    
    const onKeydown = (event) => {
      emit('keydown', event)
    }
    
    // Watch for modelValue changes and auto-clear errors
    watch(() => props.modelValue, () => {
      if (props.error) {
        clearErrors()
      }
    })
    
    return {
      passwordVisible,
      focused,
      fieldId,
      fieldClasses,
      togglePasswordVisibility,
      validateField,
      clearErrors,
      onFocus,
      onInput,
      onChange,
      onCheckboxChange,
      onKeydown
    }
  }
}
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  position: relative;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #f3f3f3;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.required-indicator {
  color: var(--tier-s-color, #ff4500);
  font-weight: bold;
}

.form-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #444;
  border-radius: 8px;
  background-color: #333;
  color: #f3f3f3;
  font-size: 1rem;
  transition: var(--tier-transition-fast, all 0.2s ease);
  outline: none;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.5;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  border-color: var(--tier-d-color, #00bfff);
  box-shadow: 0 0 0 3px var(--tier-d-alpha, rgba(0, 191, 255, 0.15));
  background-color: #3a3a3a;
}

.form-input:hover:not(:disabled),
.form-select:hover:not(:disabled),
.form-textarea:hover:not(:disabled) {
  border-color: #555;
  background-color: #3a3a3a;
}

.form-input:disabled,
.form-select:disabled,
.form-textarea:disabled {
  background-color: #2a2a2a;
  color: #888;
  cursor: not-allowed;
  border-color: #333;
}

/* Enhanced field types */
.field-type-password .form-input {
  padding-right: 3rem;
}

.field-type-select .form-input {
  cursor: pointer;
}

/* Checkbox styling */
.form-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.form-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #444;
  border-radius: 4px;
  background-color: #333;
  cursor: pointer;
  position: relative;
  margin: 0;
}

.form-checkbox:checked {
  background-color: var(--tier-d-color, #00bfff);
  border-color: var(--tier-d-color, #00bfff);
}

.form-checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: -2px;
  left: 2px;
  color: white;
  font-size: 0.875rem;
  font-weight: bold;
}

.checkbox-label {
  color: #f3f3f3;
  cursor: pointer;
  font-size: 0.875rem;
  margin: 0;
}

/* Password toggle */
.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  user-select: none;
  z-index: 2;
}

.password-toggle-icon {
  font-size: 1.25rem;
  opacity: 0.7;
  transition: var(--tier-transition-fast, all 0.2s ease);
}

.password-toggle:hover .password-toggle-icon {
  opacity: 1;
}

/* Loading spinner */
.input-loading {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid #444;
  border-top: 2px solid var(--tier-d-color, #00bfff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* State styling */
.has-error .form-input,
.has-error .form-select,
.has-error .form-textarea,
.has-error .form-checkbox {
  border-color: var(--tier-s-color, #ff4500);
  box-shadow: 0 0 0 3px var(--tier-s-alpha, rgba(255, 69, 0, 0.15));
}

.has-success .form-input,
.has-success .form-select,
.has-success .form-textarea,
.has-success .form-checkbox {
  border-color: var(--tier-c-color, #32cd32);
  box-shadow: 0 0 0 3px var(--tier-c-alpha, rgba(50, 205, 50, 0.15));
}

.is-loading .form-input,
.is-loading .form-select {
  padding-right: 3rem;
}

/* Messages */
.form-error {
  color: var(--tier-s-color, #ff4500);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-error::before {
  content: '⚠️';
  font-size: 0.75rem;
}

.form-success {
  color: var(--tier-c-color, #32cd32);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-success::before {
  content: '✅';
  font-size: 0.75rem;
}

.form-help {
  color: #aaa;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .form-input,
  .form-select {
    font-size: 1rem; /* Prevent zoom on iOS */
    padding: 1rem 0.875rem;
    min-height: 44px; /* Minimum touch target */
  }
  
  .form-checkbox {
    min-width: 44px;
    min-height: 44px;
  }
  
  .form-field {
    margin-bottom: 1.25rem;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .form-input,
  .form-select {
    border-width: 3px;
  }
  
  .form-label {
    font-weight: 700;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .form-select,
  .password-toggle-icon,
  .loading-spinner {
    transition: none;
    animation: none;
  }
}
</style>